'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Send, Loader2, Search, Circle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_role: 'customer' | 'admin';
  sender_name: string;
  message: string;
  created_at: string;
}

interface Conversation {
  id: string;
  customer_id: string;
  customer_name: string;
  customer_email: string;
  status: string;
  last_message: string;
  last_message_at: string;
  unread_admin: number;
}

export default function AdminChatPanel() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [search, setSearch] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const supabase = createClient();

  const adminId = 'admin-001';
  const adminName = 'EzCleanCare Support';

  const scrollToBottom = useCallback(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  const loadConversations = useCallback(async () => {
    setLoadingConvs(true);
    try {
      const { data } = await supabase
        .from('chat_conversations')
        .select('*')
        .order('last_message_at', { ascending: false });
      setConversations(data || []);
    } catch (err) {
      console.error('Load convs error:', err);
    } finally {
      setLoadingConvs(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Real-time: new conversations
  useEffect(() => {
    const channel = supabase
      .channel('admin-conversations')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_conversations' }, () => {
        loadConversations();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [supabase, loadConversations]);

  const loadMessages = useCallback(async (convId: string) => {
    setLoadingMsgs(true);
    try {
      const { data } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', convId)
        .order('created_at', { ascending: true });
      setMessages(data || []);
    } catch (err) {
      console.error('Load msgs error:', err);
    } finally {
      setLoadingMsgs(false);
    }
  }, [supabase]);

  const selectConversation = async (conv: Conversation) => {
    setSelectedConv(conv);
    await loadMessages(conv.id);
    scrollToBottom();
    // Mark as read
    if (conv.unread_admin > 0) {
      await supabase.from('chat_conversations').update({ unread_admin: 0 }).eq('id', conv.id);
      setConversations((prev) => prev.map((c) => c.id === conv.id ? { ...c, unread_admin: 0 } : c));
    }
  };

  // Real-time: messages for selected conversation
  useEffect(() => {
    if (!selectedConv?.id) return;
    const channel = supabase
      .channel(`admin-msgs-${selectedConv.id}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'chat_messages',
        filter: `conversation_id=eq.${selectedConv.id}`
      }, (payload) => {
        const newMsg = payload.new as Message;
        setMessages((prev) => {
          if (prev.find((m) => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
        scrollToBottom();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [selectedConv?.id, supabase, scrollToBottom]);

  useEffect(() => {
    if (messages.length > 0) scrollToBottom();
  }, [messages, scrollToBottom]);

  const sendMessage = async () => {
    if (!input.trim() || !selectedConv?.id || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    try {
      const { error } = await supabase.from('chat_messages').insert({
        conversation_id: selectedConv.id,
        sender_id: adminId,
        sender_role: 'admin',
        sender_name: adminName,
        message: text,
      });
      if (error) throw error;
      await supabase.from('chat_conversations').update({
        last_message: text,
        last_message_at: new Date().toISOString(),
        unread_customer: 1,
      }).eq('id', selectedConv.id);
    } catch (err) {
      console.error('Send error:', err);
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_admin || 0), 0);
  const filtered = conversations.filter((c) =>
    c.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.customer_email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden flex" style={{ height: '600px' }}>
      {/* Conversations list */}
      <div className="w-72 flex-shrink-0 border-r border-border flex flex-col">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <MessageCircle size={16} className="text-primary" />
          <p className="text-sm font-bold text-foreground">Customer Chats</p>
          {totalUnread > 0 && (
            <span className="ml-auto bg-danger text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{totalUnread}</span>
          )}
        </div>
        <div className="px-3 py-2 border-b border-border">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customers..."
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-border bg-muted/40 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-border">
          {loadingConvs ? (
            <div className="flex items-center justify-center h-24">
              <Loader2 size={20} className="animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 gap-2 text-center px-4">
              <MessageCircle size={24} className="text-muted-foreground/40" />
              <p className="text-xs text-muted-foreground">No conversations yet</p>
            </div>
          ) : (
            filtered.map((conv) => (
              <button
                key={conv.id}
                onClick={() => selectConversation(conv)}
                className={`w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors ${selectedConv?.id === conv.id ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
              >
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">{conv.customer_name?.charAt(0) || 'C'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <p className="text-xs font-semibold text-foreground truncate">{conv.customer_name}</p>
                      {conv.unread_admin > 0 && (
                        <span className="w-4 h-4 bg-danger text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">{conv.unread_admin}</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.last_message || 'No messages yet'}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Circle size={6} className={conv.status === 'open' ? 'text-success fill-success' : 'text-muted-foreground fill-muted-foreground'} />
                      <p className="text-xs text-muted-foreground/60">{conv.status}</p>
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Message area */}
      <div className="flex-1 flex flex-col min-w-0">
        {!selectedConv ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-8">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <MessageCircle size={26} className="text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">Select a conversation</p>
            <p className="text-xs text-muted-foreground">Choose a customer chat from the left to view and respond to messages.</p>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-xs font-bold text-primary">{selectedConv.customer_name?.charAt(0) || 'C'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground">{selectedConv.customer_name}</p>
                <p className="text-xs text-muted-foreground">{selectedConv.customer_email}</p>
              </div>
              <button
                onClick={async () => {
                  const newStatus = selectedConv.status === 'open' ? 'closed' : 'open';
                  await supabase.from('chat_conversations').update({ status: newStatus }).eq('id', selectedConv.id);
                  setSelectedConv({ ...selectedConv, status: newStatus });
                  setConversations((prev) => prev.map((c) => c.id === selectedConv.id ? { ...c, status: newStatus } : c));
                }}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${selectedConv.status === 'open' ? 'bg-success-bg text-success hover:bg-success/20' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              >
                {selectedConv.status === 'open' ? 'Open' : 'Closed'}
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-muted/10">
              {loadingMsgs ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 size={22} className="animate-spin text-muted-foreground" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-2 text-center">
                  <p className="text-xs text-muted-foreground">No messages yet. Send the first reply!</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isAdmin = msg.sender_role === 'admin';
                  return (
                    <div key={msg.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm ${isAdmin ? 'gradient-primary text-white rounded-br-sm' : 'bg-card border border-border text-foreground rounded-bl-sm'}`}>
                        {!isAdmin && (
                          <p className="text-xs font-semibold text-primary mb-0.5">{msg.sender_name}</p>
                        )}
                        <p className="leading-relaxed break-words">{msg.message}</p>
                        <p className={`text-xs mt-1 ${isAdmin ? 'text-white/60' : 'text-muted-foreground'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="px-3 py-3 border-t border-border flex items-end gap-2 flex-shrink-0 bg-card">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a reply..."
                rows={1}
                disabled={selectedConv.status === 'closed'}
                className="flex-1 resize-none rounded-xl border border-border bg-muted/40 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 max-h-24 overflow-y-auto disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ minHeight: '38px' }}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || sending || selectedConv.status === 'closed'}
                className="w-9 h-9 rounded-xl gradient-primary text-white flex items-center justify-center hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                aria-label="Send reply"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
